// src/prototype/components/sep/SEPSectionResourcePlan.tsx
// Component 6: resource_requirements

import React from 'react';
import { Users } from 'lucide-react';
import type { StrategicExecutionPlanSections } from '../../services/strategicExecutionPlanService';
import type { MappedResourcePlanInitiative } from '../../utils/mapStrategicExecutionPlan';

export interface SEPSectionResourcePlanProps {
  clientName: string;
  initiatives: MappedResourcePlanInitiative[];
  capacityPlanningNotes?: NonNullable<StrategicExecutionPlanSections['resource_requirements']>['capacity_planning_notes'];
}

const DEFAULT_CAPACITY_NOTES = [
  'FTE estimates represent average engagement across the initiative timeline, not peak sprint demand.',
  'Quick Win initiatives can typically leverage existing team capacity with minimal backfill requirements.',
  'Complex initiatives may require phased staffing ramp to align with delivery milestones.',
  'External vendor or consulting support should be scoped for initiatives exceeding 1.5 FTE demand.',
];

export const SEPSectionResourcePlan: React.FC<SEPSectionResourcePlanProps> = ({
  clientName, initiatives, capacityPlanningNotes,
}) => {
  const capacityNotes = capacityPlanningNotes?.length
    ? capacityPlanningNotes
    : DEFAULT_CAPACITY_NOTES;

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl p-6">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-[3px] self-stretch rounded-full bg-[#FF6E4C] shrink-0 min-h-[40px]" />
        <div>
          <p className="text-[10px] font-bold text-[#FF6E4C] uppercase tracking-widest mb-0.5">Section 6</p>
          <h2 className="text-lg font-bold text-[#18365E] leading-tight">Resource & Capacity Plan</h2>
          <p className="text-[11px] text-[#717182] mt-0.5">FTE demand by role across the initiative portfolio</p>
        </div>
      </div>

      <p className="text-sm text-[#18365E]/80 leading-relaxed mb-6">
        The resource plan below outlines the estimated FTE requirements for each initiative at <strong>{clientName}</strong>.
        Staffing models are derived from initiative complexity and scope, and assume engagement of existing internal teams supplemented by targeted external expertise where noted.
      </p>

      <div className="space-y-5">
        {initiatives.map(initiative => (
          <div key={initiative.id} className="border border-[#e5e7eb] rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-[#F5F7FA] border-b border-[#e5e7eb]">
              <div className="flex items-center gap-2.5">
                <div className="w-6 h-6 rounded-full bg-[#18365E] flex items-center justify-center">
                  <span className="text-[10px] font-bold text-white">{initiative.index}</span>
                </div>
                <p className="text-sm font-semibold text-[#18365E]">{initiative.title}</p>
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border bg-[#18365E]/5 border-[#18365E]/15 text-[#18365E]">
                <Users className="w-3 h-3" />
                {initiative.fteTotal} FTE Total
              </div>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#e5e7eb]">
                  <th className="text-left px-4 py-2 font-semibold text-[#717182]">Role Name</th>
                  <th className="text-left px-3 py-2 font-semibold text-[#717182]">Team/Department</th>
                  <th className="text-center px-3 py-2 font-semibold text-[#717182]">FTE Count</th>
                  <th className="text-left px-3 py-2 font-semibold text-[#717182]">Role Scope</th>
                </tr>
              </thead>
              <tbody>
                {initiative.roles.map((role, ri) => (
                  <tr key={ri} className={ri < initiative.roles.length - 1 ? 'border-b border-[#e5e7eb]/60' : ''}>
                    <td className="px-4 py-2.5 font-medium text-[#18365E]">{role.role}</td>
                    <td className="px-3 py-2.5 text-[#717182]">{role.team}</td>
                    <td className="px-3 py-2.5 text-center font-semibold text-[#18365E]">{role.fte}</td>
                    <td className="px-3 py-2.5 text-[#717182]">{role.scope}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <div className="mt-5 bg-[#F5F7FA] border border-[#e5e7eb] rounded-xl p-4">
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-2">Capacity Planning Notes</p>
        <div className="space-y-1.5">
          {capacityNotes.map((note, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className="w-1 h-1 rounded-full bg-[#FF6E4C] mt-1.5 shrink-0" />
              <p className="text-xs text-[#18365E]/75 leading-relaxed">{note}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SEPSectionResourcePlan;
